import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef } from "react";
import PropTypes from "prop-types";
import DataGrid, {
    Column,
    Editing,
    Paging,
    PatternRule,
    RequiredRule,
    StringLengthRule,
    NumericRule,
    Selection,
    Lookup,
    CustomRule,
    Button,
    FormItem,
    Scrolling,
} from 'devextreme-react/data-grid';
import 'devextreme-react/text-area';
import esMessages from "./esMessages.json";
import { locale, loadMessages } from "devextreme/localization";
import EmployeeTagBoxComponent from "./components/EmployeeTagBoxComponent.js"
import { getLocalizedString, language } from "@icarius-localization/strings";
import { getFormat, getEditorOptions, decimals } from './utils/gridConfig';
import { displayMultipleSelectData } from './utils/gridDisplay';
import { onFocusedCellChanged, onEditorPrepared, onEditorPreparing } from './utils/gridEvents';
import * as gridValidations from './utils/gridValidations';
import { formatNumberExactDecimals } from "@icarius-utils/format";
import { onToolbarpreparing } from './utils/gridEvents';

const notesEditorOptions = { height: 100 };

function getMultipleSelectDisplayValue(field) {
    switch (field) {
        case "codeRef":
            return (rowData) => displayMultipleSelectData(rowData, 'codeRef');
        case "calculationIndicator":
            return (rowData) => displayMultipleSelectData(rowData, 'calculationIndicator');
        case "entryMode":
            return (rowData) => displayMultipleSelectData(rowData, 'entryMode');
        case "concepts":
            return (rowData) => displayMultipleSelectData(rowData, 'concepts');
        default:
            return null;
    }
}

const EditableGridV2 = forwardRef(function EditableGridV2(props, ref) {
    const {
        columns,
        selectDataSources,
        gridName,
        batchMode,
        nestedGrid,
        rowData,
        shouldReloadGrid,
        setShouldReloadGrid,
        setDataChanged,
        getUnAuthData,
        getHHMMforMinutes,
        getMinutesForHHMM,
    } = props;

    const dataGridRef = useRef(null);
    const [originalData, setOriginalData] = useState(rowData);

    // Localization
    if (language === "es") {
        loadMessages(esMessages);
        locale("es");
    }

    // Handle reload
    useEffect(() => {
        if (shouldReloadGrid) {
            setOriginalData(rowData);
            setShouldReloadGrid && setShouldReloadGrid(false);
        }
    }, [shouldReloadGrid, rowData, setShouldReloadGrid]);

    // Compare data
    const getVisibleRows = useCallback(() => {
        if (dataGridRef.current && dataGridRef.current.instance) {
            return dataGridRef.current.instance.getVisibleRows();
        }
        return [];
    }, []);

    const compareDataSources = (o1, o2) => {
        for (let p in o1) {
            if (o1.hasOwnProperty(p)) {
                if (o1[p] !== o2[p]) {
                    return true;
                }
            }
        }
        for (let p in o2) {
            if (o2.hasOwnProperty(p)) {
                if (o1[p] !== o2[p]) {
                    return true;
                }
            }
        }
        return false;
    };

    const compareData = useCallback(() => {
        const dataChanged = compareDataSources(originalData, getVisibleRows());
        setDataChanged && setDataChanged(dataChanged);
    }, [originalData, getVisibleRows, setDataChanged]);

    // Sorting
    const sortStringsConsideringCulture = (value1, value2, lookup) => {
        if (lookup && lookup.valueExpr === "key") {
            if (lookup.valueMap && lookup.valueMap.hasOwnProperty(value1)) {
                value1 = lookup.valueMap[value1];
            }

            if (lookup.valueMap && lookup.valueMap.hasOwnProperty(value2)) {
                value2 = lookup.valueMap[value2];
            }
        }

        if (!isNaN(value1) && !isNaN(value2)) {
            value1 = Number(value1);
            value2 = Number(value2);
            return value1 - value2;
        }

        // Handling null values
        if (!value1 && value2) return -1;
        if (!value1 && !value2) return 0;
        if (value1 && !value2) return 1;
        // Determines whether two strings are equivalent in the current locale
        return value1.localeCompare(value2);
    };

    // Funciones utilitarias para columnas
    const getFilteredConcepts = (options) => {
        return {
            store: selectDataSources("all-concepts"),
            filter: options.data ? ['Tipo de concepto', '=', options.data["Tipo de concepto"]] : null,
        };
    };

    const setStateValue = (rowData, value, columnName) => {
        rowData["Concepto"] = null;
        dataGridRef.current.instance.columnOption(columnName).defaultSetCellValue(rowData, value);
    };

    const setHoursMinutes = (rowData, value, columnName) => {
        if (value !== undefined && value !== null && /^(\d|0\d|1\d|2[0-3]):[0-5]\d$/.test(value)) {
            let arr = value.split(':');
            let dec = parseInt((arr[1] / 6) * 10, 10);

            const newValue = parseFloat(parseInt(arr[0], 10) + '.' + (dec < 10 ? '0' : '') + dec);

            rowData["Cantidad"] = formatNumberExactDecimals(newValue, 3);
        }

        dataGridRef.current.instance.columnOption(columnName).defaultSetCellValue(rowData, value);
    };

    const setBankValue = (rowData, value, columnName) => {
        rowData["Banco"] = null;
        rowData["Cuenta bancaria"] = null;
        dataGridRef.current.instance.columnOption(columnName).defaultSetCellValue(rowData, value);
    };

    const renderColumns = () => {
        return columns.map((item, index) => {
            const isRequired = item.isObligatory ? <RequiredRule /> : null;
            const lengthRule = item.minLength !== null && item.maxLength ? <StringLengthRule min={item.minLength} max={item.maxLength} /> : null;
            const patternRule = item.pattern ? <PatternRule pattern={item.pattern} /> : null;
            const numericRule = (item.isNumeric && !item.validationUnrequired) ? <NumericRule /> : null;
            const lookupColumn = item.isSelect ? <Lookup dataSource={selectDataSources(item.field)} valueExpr="key" displayExpr="value" /> : null;
            let isVisible = true;
            if (
                ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                [getLocalizedString("comments"), getLocalizedString("incomeDate"), getLocalizedString("outcomeDate"), getLocalizedString("paymentToThirdPartyComments")].includes(item.headerName)
            ) {
                isVisible = false;
            }
            let multipleSelectDisplayValue = item.isMultipleSelect ? getMultipleSelectDisplayValue(item.field) : null;
            let props = {
                key: index,
                caption: item.headerName,
                dataField: item.field,
                dataType: item.dataType,
                format: getFormat(gridName, item),
                editorOptions: getEditorOptions(gridName, item),
                editCellComponent: item.isMultipleSelect ? EmployeeTagBoxComponent : null,
                calculateDisplayValue: multipleSelectDisplayValue,
                fixed: item.isFixed ? true : false,
                fixedPosition: "left",
                allowEditing: item.disabled === true ? false : true,
                visible: isVisible,
                getFilteredConcepts,
                setStateValue: (rowData, value) => setStateValue(rowData, value, item.field),
                setHoursMinutes: (rowData, value) => setHoursMinutes(rowData, value, item.field),
                setBankValue: (rowData, value) => setBankValue(rowData, value, item.field),
            };
            if (batchMode) {
                if (nestedGrid) {
                    if (item.headerName === getLocalizedString("installment")) {
                        props.width = 50;
                    } else if ([getLocalizedString("state"), getLocalizedString("quotaValue")].includes(item.headerName)) {
                        props.width = 150;
                    } else {
                        props.width = 300;
                    }
                } else {
                    if (
                        gridName === "extraHoursAuthorization" &&
                        [getLocalizedString("date"), getLocalizedString("authorizedExtraHours"), getLocalizedString("overtimeHoursPerformed"), getLocalizedString("unauthorizedOvertimeShortDots")].includes(item.headerName)
                    ) {
                        props.width = 180;
                    } else if (item.isNumeric) {
                        props.width = 200;
                    } else if (item.headerName === getLocalizedString("employeeCode")) {
                        props.width = 160;
                    } else {
                        props.width = 300;
                    }
                }
            } else {
                if (item.headerName === getLocalizedString("overtimeCode")) {
                    props.width = 200;
                }
            }
            if (item.headerName === getLocalizedString("conceptType")) {
                props.setCellValue = props.setStateValue;
            }
            if (item.headerName === getLocalizedString("paymentMethod")) {
                props.setCellValue = props.setBankValue;
            }
            if (item.headerName === getLocalizedString("hoursAndMinutesHhmmColon") || item.headerName.includes("(hhmm)") || item.headerName.includes("Hrs. ")) {
                props.setCellValue = props.setHoursMinutes;
            }
            if (
                gridName === "entryPerConceptSet" &&
                !["Código de empleado", "En fecha", "Apellido y nombres"].includes(props.dataField)
            ) {
                props.customizeText = function (cellInfo) {
                    if (
                        cellInfo.value === null ||
                        cellInfo.value === "0" ||
                        cellInfo.value === 0.00 ||
                        cellInfo.value === "0:00" ||
                        cellInfo.value === "00:00" ||
                        cellInfo.value === 0
                    ) return '';
                    return cellInfo.value;
                }
            }

            return (
                <Column
                    {...props}
                    sortingMethod={sortStringsConsideringCulture}
                >
                    {
                        [getLocalizedString("comments"), getLocalizedString("paymentToThirdPartyComments")].includes(item.headerName) &&
                        <FormItem colSpan={2} editorType="dxTextArea" editorOptions={notesEditorOptions} />
                    }
                    {isRequired}
                    {patternRule}
                    {lengthRule}
                    {numericRule}
                    {
                        item.headerName === getLocalizedString("concept") ?
                            <Lookup dataSource={(options) => props.getFilteredConcepts(options)} valueExpr="key" displayExpr="value" /> :
                            lookupColumn
                    }
                    {
                        item.headerName === getLocalizedString("reportedValueType") &&
                        <CustomRule
                            message={getLocalizedString("obligatoryValueType")}
                            validationCallback={gridValidations.customCallbackInformedValueType}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "holidayTable" &&
                        item.headerName === getLocalizedString("seniorityUntilYears") &&
                        <CustomRule
                            message={getLocalizedString("seniorityError")}
                            validationCallback={gridValidations.customCallbackSeniorityTo}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "holidayTable" && item.headerName !== getLocalizedString("tableType") &&
                        <CustomRule
                            message={getLocalizedString("valueMustBeGreaterThanZeroTableType")}
                            validationCallback={gridValidations.customCallbackHolidayTableGreaterEqualZero}
                            reevaluate={true}
                        />
                    }
                    {

                        ["extraHoursAuthorization", "entryPerConcept", "dataCapture", "massEntry", "entryPerSociety", "entryPerConceptSet"].includes(gridName)
                        && (item.headerName === getLocalizedString("hoursAndMinutesHhmmColon") || item.headerName.includes("(hh:mm)") || item.headerName.includes("(hhmm)") || item.headerName.includes("Hrs. ")) &&
                        <CustomRule
                            message={getLocalizedString("checkTimeFormat")}
                            validationCallback={gridValidations.customCallbackEntryPerConceptHoursMinutesValidSyntax}
                            reevaluate={true}
                        />
                    }
                    {
                        ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                        (item.headerName === getLocalizedString("hoursAndMinutesHhmmColon") || item.headerName.includes("(hhmm)")) &&
                        <CustomRule
                            message={getLocalizedString("timeGreaterThanCollaboratorWorktime")}
                            validationCallback={gridValidations.customCallbackEntryPerConceptHoursMinutes}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "extraHoursAuthorization" &&
                        item.headerName === getLocalizedString("authorizedExtraHours") &&
                        <CustomRule
                            message={getLocalizedString("timeGreaterThan2359")}
                            validationCallback={gridValidations.customCallbackEntryPerConceptHoursMinutesNoData}
                            reevaluate={true}
                        />
                    }
                    {
                        ["distributionCost", "entryByInstallments", "dataCapture", "massEntry", "entryPerSociety", "entryPerConceptSet"].includes(gridName) &&
                        item.headerName === getLocalizedString("percentage") &&
                        <CustomRule
                            message={item.isObligatory ?
                                getLocalizedString("commaSeparatorThreeDecimalsNoZero") :
                                getLocalizedString("commaSeparatorThreeDecimals")}
                            validationCallback={item.isObligatory ? gridValidations.customCallbackMax3Decimals : gridValidations.customCallbackMax3DecimalsAllowZero}
                            reevaluate={true}
                        />
                    }
                    {
                        ["entryByInstallments", "dataCapture", "massEntry", "entryPerSociety", "entryPerConceptSet"].includes(gridName) &&
                        (item.headerName === getLocalizedString("cant") || item.isQuantityEntryPerConceptSet) &&
                        <CustomRule
                            message={item.isObligatory ?
                                getLocalizedString("commaSeparatorFiveDecimalsNoZero") :
                                getLocalizedString("commaSeparatorFiveDecimals")}
                            validationCallback={item.isObligatory ? gridValidations.customCallbackMax5Decimals : gridValidations.customCallbackMax5DecimalsAllowZero}
                            reevaluate={true}
                        />
                    }
                    {
                        ["dataCapture", "massEntry", "entryPerSociety", "entryPerConceptSet"].includes(gridName) &&
                        item.headerName !== getLocalizedString("cant") && !item.isQuantityEntryPerConceptSet &&
                        (
                            item.headerName === getLocalizedString("grossReportedValue") &&
                            (item.isValueEntryPerConceptSet || item.isNumeric)
                        ) &&
                        <CustomRule
                            message={item.isObligatory ?
                                getLocalizedString("commaSeparatorTwoDecimalsNoZero") :
                                getLocalizedString("commaSeparatorTwoDecimals")}
                            validationCallback={item.isObligatory ? gridValidations.customCallbackMax2DecimalsAllowNegative : gridValidations.customCallbackMax2DecimalsAllowZeroAllowNegative}
                            reevaluate={true}
                        />
                    }
                    {
                        ["dataCapture", "massEntry", "entryPerSociety", "entryPerConceptSet"].includes(gridName) &&
                        item.headerName !== "Cantidad" && !item.isQuantityEntryPerConceptSet &&
                        (
                            [getLocalizedString("liquidReportedValue"), getLocalizedString("percentage"), getLocalizedString("calculationBase"), getLocalizedString("quotaValue")].includes(item.headerName) ||
                            (item.isValueEntryPerConceptSet && item.headerName !== getLocalizedString("grossReportedValue")) ||
                            (item.isNumeric && item.headerName !== getLocalizedString("grossReportedValue"))
                        ) &&
                        <CustomRule
                            message={item.isObligatory ?
                                getLocalizedString("commaSeparatorTwoDecimalsNoZero") :
                                getLocalizedString("commaSeparatorTwoDecimals")}
                            validationCallback={item.isObligatory ? gridValidations.customCallbackMax2Decimals : gridValidations.customCallbackMax2DecimalsAllowZero}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "entryPerConcept" &&
                        [getLocalizedString("cant"), getLocalizedString("grossReportedValue"), getLocalizedString("liquidReportedValue"), getLocalizedString("percentage"), getLocalizedString("calculationBase")].includes(item.headerName) &&
                        <CustomRule
                            message={item.isObligatory ?
                                getLocalizedString("commaSeparatorGenericDecimalsNoZero", { number: decimals[item.headerName] }) :
                                getLocalizedString("commaSeparatorGenericDecimals", { number: decimals[item.headerName] })}
                            validationCallback={
                                item.isObligatory ?
                                    () => gridValidations.customCallbackMax2Decimals(decimals[item.headerName]) :
                                    () => gridValidations.customCallbackMax2DecimalsAllowZero(decimals[item.headerName])
                            }
                            reevaluate={true}
                        />
                    }
                    {
                        (gridName === "extraHoursTable" && item.headerName === getLocalizedString("overtimeCode")) &&
                        <CustomRule
                            message={getLocalizedString("extraHoursTableInvalidCodeMessage")}
                            validationCallback={gridValidations.customCallbackExtraHoursTableCode}
                            reevaluate={true}
                        />
                    }

                    {
                        ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                        (
                            [getLocalizedString("employeesProcessPeriodoReliquidacion"), getLocalizedString("allocationPeriod"), getLocalizedString("periodFrom"), getLocalizedString("periodUntil")].includes(item.headerName) ||
                            item.headerName.includes(getLocalizedString("period"))
                        ) &&
                        <CustomRule
                            message={getLocalizedString("invalidPeriodMessage")}
                            validationCallback={gridValidations.customCallbackPeriodInverted}
                            reevaluate={true}
                        />
                    }
                    {
                        ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                        item.headerName.includes(getLocalizedString("periodFrom")) &&
                        <CustomRule
                            message={getLocalizedString("assetsDiscountsDateStartLessThan")}
                            validationCallback={gridValidations.customCallbackPeriodFromTo}
                            reevaluate={true}
                        />
                    }
                    {
                        ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                        item.headerName.includes(getLocalizedString("periodUntil")) &&
                        <CustomRule
                            message={getLocalizedString("assetsDiscountsDateEndGreaterThan")}
                            validationCallback={gridValidations.customCallbackPeriodFromTo}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "festivitiesTable" && item.headerName === getLocalizedString("year") &&
                        <CustomRule
                            message={getLocalizedString("invalidYearSelected")}
                            validationCallback={gridValidations.customCallbackYear4Digits}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "festivitiesTable" && item.headerName === getLocalizedString("startDate") &&
                        <CustomRule
                            message={getLocalizedString("startDateMustBeGreaterThanEndDate")}
                            validationCallback={gridValidations.customCallbackDateStartEnd}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "festivitiesTable" && (item.headerName === getLocalizedString("startDate") || item.headerName === getLocalizedString("endDateAlt")) &&
                        <CustomRule
                            message={getLocalizedString("checkYearFestivitiesDate")}
                            validationCallback={gridValidations.customCallbackDateStartEndYear}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "festivitiesTable" && item.headerName === getLocalizedString("endDateAlt") &&
                        <CustomRule
                            message={getLocalizedString("endDateErrorFestiviesTable")}
                            validationCallback={gridValidations.customCallbackDateStartEnd}
                            reevaluate={true}
                        />
                    }
                    {
                        ["distributionCost", "dataCapture", "massEntry", "entryPerSociety", "entryPerConceptSet"].includes(gridName) &&
                        (item.headerName === getLocalizedString("percentage") || item.isPercentageEntryPerConceptSet) &&
                        <CustomRule
                            message={getLocalizedString("enterValidPercentage")}
                            validationCallback={item.isObligatory ? gridValidations.customCallbackPercentage : gridValidations.customCallbackPercentageAllowZero}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "entryPerSociety" && item.headerName === getLocalizedString("concept") &&
                        <CustomRule
                            message={getLocalizedString("duplicatedDataEntryPerSociety")}
                            validationCallback={(options) => gridValidations.customCallbackEntryPerSocietyDuplicatedConcept(options, getVisibleRows)}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "extraHoursAuthorization" &&
                        (item.headerName === getLocalizedString("employeeCode") || item.headerName === getLocalizedString("date")) &&
                        <CustomRule
                            message={getLocalizedString("duplicatedDataExtraHoursAuthorization")}
                            validationCallback={(options) => gridValidations.customCallbackExtraHoursAuthorizationDuplicatedEmployeeDate(options, getVisibleRows)}
                            reevaluate={true}
                        />
                    }
                    {
                        ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                        item.headerName === getLocalizedString("bank") &&
                        <CustomRule
                            message={getLocalizedString("assetsDiscountsRequiresBank")}
                            validationCallback={gridValidations.customCallbackValidatePaymentTypeBank}
                            reevaluate={true}
                        />
                    }
                    {
                        ["entryPerConcept", "dataCapture", "massEntry", "entryPerSociety"].includes(gridName) &&
                        item.headerName === getLocalizedString("bankAccount") &&
                        <CustomRule
                            message={getLocalizedString("assetsDiscountsRequiresBankAccount")}
                            validationCallback={gridValidations.customCallbackValidatePaymentTypeCC}
                            reevaluate={true}
                        />
                    }
                    {
                        gridName === "distributionCost" &&
                        [getLocalizedString("employeesProcessCentroBeneficio"), getLocalizedString("employeesProcessProyecto"), getLocalizedString("employeesProcessDimension2"), getLocalizedString("employeesProcessDimension3"), getLocalizedString("employeesProcessDimension4"), getLocalizedString("employeesProcessDimension5")].includes(item.headerName) &&
                        <CustomRule
                            message={getLocalizedString("distributionCostInsertValue")}
                            validationCallback={gridValidations.customCallbackProjectBenefitCenter}
                            reevaluate={true}
                        />
                    }
                </Column>
            );
        });
    };

    // Custom properties
    const customProperties = useMemo(() => {
        if (batchMode) {
            return {
                columnMinWidth: 60
            };
        } else {
            return {
                columnResizingMode: "nextColumn",
                wordWrapEnabled: true,
                columnMinWidth: 30
            };
        }
    }, [batchMode]);

    return (
        <div id="data-grid-demo" style={{ marginBottom: 20 }}>
            <DataGrid
                ref={node => {
                    dataGridRef.current = node;
                    if (ref) {
                        if (typeof ref === 'function') {
                            ref(node?.instance);
                        } else {
                            ref.current = node?.instance;
                        }
                    }
                }}
                dataSource={originalData}
                onRowInserted={compareData}
                onToolbarPreparing={batchMode ? onToolbarpreparing : null}
                onRowUpdated={compareData}
                onRowRemoved={compareData}
                showBorders={false}
                allowColumnResizing={true}
                columnAutoWidth={true}
                width={"91vw"}
                onEditorPreparing={(options) => onEditorPreparing(options, selectDataSources, getUnAuthData, getHHMMforMinutes, getMinutesForHHMM, compareData)}
                onFocusedCellChanged={onFocusedCellChanged}
                onEditorPrepared={(options) => onEditorPrepared(options, selectDataSources)}
                rowAlternationEnabled={true}
                style={{ margin: "0 auto" }}
                {...customProperties}
            >
                {
                    !nestedGrid &&
                    <Selection mode="multiple" showCheckBoxesMode="always" />
                }
                <Paging enabled={false} />
                {
                    batchMode &&
                    <Scrolling mode="virtual" />
                }
                <Editing
                    mode={batchMode ? "batch" : "form"}
                    allowUpdating={true}
                    allowDeleting={false}
                    selectTextOnEditStart={Boolean(batchMode)}
                />
                {renderColumns()}
                {
                    !batchMode &&
                    <Column type="buttons" caption={getLocalizedString("actionsShort")} fixed={true} fixedPosition="right">
                        <Button name="edit" />
                    </Column>
                }
            </DataGrid>
        </div>
    );
});

EditableGridV2.propTypes = {
    columns: PropTypes.array.isRequired,
    selectDataSources: PropTypes.func,
    gridName: PropTypes.string.isRequired,
    batchMode: PropTypes.bool,
    nestedGrid: PropTypes.bool,
    rowData: PropTypes.array.isRequired,
    shouldReloadGrid: PropTypes.bool,
    setShouldReloadGrid: PropTypes.func,
    setDataChanged: PropTypes.func
};

EditableGridV2.defaultProps = {
    selectDataSources: () => []
};

export default EditableGridV2;